import React from "react";
import {
  Chip,
  Divider,
  Grid,
  Typography,
  Stack,
  Paper,
  Button,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";

import { useNavigate } from "react-router-dom";

import UserChip from "../../../components/chips/user-chip";
import RoleChip from "../../../components/chips/role-chip";
import TeamChip from "../../../components/chips/team-chip";
import { Organisation, User } from "../../../types";
import { getTeamRoleInfo } from "../../../utils/get-team-role-info";

interface TeamCardProps {
  team: Organisation;
  users: Record<string, User>;
  showManageButton?: boolean;
}

const TeamCard: React.FC<TeamCardProps> = ({
  team,
  users,
  showManageButton = true,
}) => {
  const navigate = useNavigate();

  // Filter users for this team.
  const teamUsers =
    team.usersIds
      ?.map((userId) => users[userId])
      .filter((user): user is User => Boolean(user)) || [];

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
      <Paper
        sx={{
          p: 2,
          borderRadius: "0.5rem",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: 4,
          },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack spacing={1}>
          {/* Header with name and type */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <TeamChip
              teamId={team.organisationId}
              name={team.name}
              color={team.color}
            />
            {!team.meetingViews || team.meetingViews.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.8rem" }}
              >
                No Layouts
              </Typography>
            ) : (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {team.meetingViews.map((meetingView) => (
                  <Chip
                    key={meetingView}
                    variant="outlined"
                    label={
                      meetingView.charAt(0).toUpperCase() + meetingView.slice(1)
                    }
                    size="small"
                    sx={{ fontSize: "0.8rem" }}
                  />
                ))}
              </Stack>
            )}
          </Stack>

          <Divider />

          <Stack spacing={0.75} sx={{ flex: 1 }}>
            {teamUsers.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.8rem" }}
              >
                No Users
              </Typography>
            ) : (
              teamUsers.map((user) => {
                return (
                  <UserChip
                    key={user.userId}
                    name={user?.fullName || user?.email || user?.userId}
                    avatarColor="#64748b"
                    roleChip={
                      <RoleChip
                        role={getTeamRoleInfo(user, team.organisationId)}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.58rem",
                          borderRadius: "999px",
                          "& .MuiChip-label": {
                            px: 0.75,
                          },
                        }}
                      />
                    }
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      height: 30,
                      "& .MuiChip-label": {
                        px: 0.75,
                        minWidth: 0,
                      },
                    }}
                  />
                );
              })
            )}
          </Stack>

          {/* Manage button */}
          {showManageButton && (
            <Stack spacing={1}>
              <Divider />
              <Button
                variant="contained"
                size="small"
                fullWidth
                startIcon={<SettingsIcon />}
                sx={{
                  mt: "auto",
                  fontSize: "0.8rem",
                  py: 0.5,
                  borderRadius: 999,
                }}
                onClick={() => navigate(`/team?teamId=${team.organisationId}`)}
              >
                Manage
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Grid>
  );
};

export default TeamCard;
